import { useState, useCallback } from 'react';

// Local interface for Google Places predictions
interface PlacePrediction {
  place_id: string;
  description: string;
  structured_formatting?: {
    main_text: string;
    secondary_text: string;
  };
}

export interface PlaceDetails {
  formatted_address?: string;
  name: string;
  place_id: string;
  geometry?: {
    location: {
      lat(): number;
      lng(): number;
    };
  };
  utc_offset_minutes?: number;
  address_components?: Array<{
    types: string[];
    long_name: string;
    short_name: string;
  }>;
}

interface UseGooglePlacesProps {
  onPlaceDetailsRequestSuccess: (placeDetails: PlaceDetails) => void;
  onPlaceDetailsRequestError: (error: Error) => void;
}

export interface FetchPredictionsParams {
  searchTerm: string;
  placeTypes?: string[];
}

export interface FetchPlaceDetailsParams {
  placeId: string;
}

// Declare Google Maps types
declare global {
  interface Window {
    google?: {
      maps: {
        places: {
          AutocompleteService: new () => {
            getPlacePredictions(
              request: {
                input: string;
                types?: string[];
              },
              callback: (
                predictions: PlacePrediction[] | null,
                status: string
              ) => void
            ): void;
          };
          PlacesService: new (div: HTMLElement) => {
            getDetails(
              request: {
                placeId: string;
                fields: string[];
              },
              callback: (place: PlaceDetails | null, status: string) => void
            ): void;
          };
          PlacesServiceStatus: {
            OK: string;
          };
        };
      };
    };
  }
}

export const useGooglePlaces = ({
  onPlaceDetailsRequestSuccess,
  onPlaceDetailsRequestError,
}: UseGooglePlacesProps) => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  const [loadingPlaceDetails, setLoadingPlaceDetails] = useState(false);

  const fetchPlacePredictions = useCallback(
    ({ searchTerm, placeTypes }: FetchPredictionsParams) => {
      if (!searchTerm.trim()) {
        setPredictions([]);
        return;
      }

      setLoadingPredictions(true);

      // Check if Google Places API is loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        const service = new window.google.maps.places.AutocompleteService();
        const request = {
          input: searchTerm,
          types: placeTypes || ['establishment'],
        };

        service.getPlacePredictions(request, (results, status) => {
          setLoadingPredictions(false);
          if (
            status === window?.google?.maps?.places?.PlacesServiceStatus?.OK &&
            results
          ) {
            setPredictions(results);
          } else {
            // Log the status for debugging
            // eslint-disable-next-line no-console
            console.warn('Google Places API error:', status);
            setPredictions([]);
          }
        });
      } else {
        // Fallback to mock data if Google Places API is not loaded
        setTimeout(() => {
          setPredictions([
            {
              place_id: `place_${Date.now()}`,
              description: `${searchTerm} - Sample Business, Sample City`,
              structured_formatting: {
                main_text: `${searchTerm} Sample Business`,
                secondary_text: 'Sample Address, Sample City',
              },
            },
          ]);
          setLoadingPredictions(false);
        }, 500);
      }
    },
    []
  );

  const fetchPlaceDetails = useCallback(
    ({ placeId }: FetchPlaceDetailsParams) => {
      setLoadingPlaceDetails(true);

      // Check if Google Places API is loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
        const request = {
          placeId,
          fields: [
            'name',
            'formatted_address',
            'geometry',
            'place_id',
            'address_components',
            'utc_offset_minutes',
          ],
        };

        service.getDetails(request, (place, status) => {
          setLoadingPlaceDetails(false);
          if (
            status === window?.google?.maps?.places?.PlacesServiceStatus?.OK &&
            place
          ) {
            onPlaceDetailsRequestSuccess(place);
          } else {
            // eslint-disable-next-line no-console
            console.warn('Google Places API error:', status);
            onPlaceDetailsRequestError(
              new Error(`Failed to fetch place details: ${status}`)
            );
          }
        });
      } else {
        // Fallback when Google Places API is not available
        setLoadingPlaceDetails(false);
        onPlaceDetailsRequestError(
          new Error('Google Places API not available')
        );
      }
    },
    [onPlaceDetailsRequestSuccess, onPlaceDetailsRequestError]
  );

  return {
    predictions,
    loadingPredictions,
    loadingPlaceDetails,
    fetchPlacePredictions,
    fetchPlaceDetails,
  };
};
